import type { ReactNode } from 'react';
import { Button } from '../Button.tsx';
import { blankLine, type BlankKind, type BlankWords } from './fault.ts';
import { unread as noneAnswered, type Read } from './read.ts';
import { useSupply } from './sources.ts';
import styles from './states.module.css';

/* ==========================================================================
   Empty is six different facts, and they need six different sentences — but
   only six, written once, here.

     nothing    the thing exists and has none of these yet
                  "No commands registered."
     filtered   there are rows; this filter is hiding all of them. The count
                and a way out are the point — an operator who reads "No
                robots" with a status filter still set goes looking for an
                outage that is not there
                  "No robots match this filter."  30 registered · 0 shown
     never      the object has never produced this class of data at all, which
                is a fact about the robot, not about the console
                  "jetrover-08 has never reported telemetry."
     awaiting   the platform does not carry it yet. Confident, not apologetic,
                and never a mock of the eventual content
                  "Live video is not carried on this platform yet."
     missing    the API answered, completely and correctly, and the object is
                not there. A 404 is not a fault and it is not an outage — the
                console is working perfectly and the id is wrong — so it reads
                as an absence, with the endpoint and status in mono and the
                way out as the only verb
                  "jetrover-99 is not on record."  /api/robots/… · 404
     withheld   the API answered 403. The account cannot read this; no retry
                changes an entitlement
                  "This account cannot read the fleet register."

   ---------------------------------------------------------------------------
   ONE PHRASING PER FACT
   ---------------------------------------------------------------------------
   The console had four sentences for one idea — "No robots on the roster." /
   "No robots registered." / "No commands match." / "No commands issued to this
   robot." — which is four different consoles as far as an operator scanning
   six panels is concerned. `blankLine` below is the only place an empty
   sentence is written; a surface names the noun and the scope and gets the
   house sentence back. Free-form `children` still work for the handful of
   absences that genuinely are not one of the six, and they should stay a
   handful.

   All six render as one line of --text-mid at the first row's position, at
   that row's height, INSIDE the panel's instrument (see ReadPanel) so the
   panel is the same size, shape and instrument empty as it is full. No
   illustration, no emoji, no exclamation mark, no headline, no centred call to
   action.
   ========================================================================== */

export type { BlankKind, BlankWords };

export function Blank({
  kind = 'nothing',
  detail,
  action,
  of,
  children,
  ...words
}: BlankWords & {
  kind?: BlankKind | undefined;
  /**
   * The read this absence is a claim about. Pass it and the sentence is
   * withdrawn whenever THAT source has not answered, whatever the rest of the
   * console is doing — see the note below on why `starved` alone is not
   * enough. A `Blank` rendered as `ReadBody`'s `empty` does not need it: the
   * body only reaches that branch on a read that landed.
   */
  of?: Read | readonly Read[] | undefined;
  /** The mono half: the filter that is excluding, the range, the reason. */
  detail?: ReactNode;
  /** At most one verb, right-aligned. Only ever the way out of a filter. */
  action?: { label: string; onSelect: () => void } | undefined;
  /** An absence that genuinely is not one of the six. Prefer the six. */
  children?: ReactNode;
}) {
  /* AN OUTAGE MAY NOT ASSERT AN ABSENCE.

     `No commands in this window.` and `No robots registered.` are claims about
     the fleet, and they are only true if something answered. A surface where
     not one mounted source is replying has read nothing, so it knows nothing,
     and a sentence that says otherwise is the single most damaging thing this
     module can put on screen: an operator reads "no faults" and stands down.

     The two absences that survive are the ones the API stated itself — a 404
     and a 403 are complete, correct answers, and they stay true whatever else
     on the surface has stopped. Everything else falls back to the house line
     for "we have not read this", which is the same words, the same ink and the
     same position the panel would show with no caller sentence at all.

     `starved` IS NOT ENOUGH ON ITS OWN, and this is the case that matters:
     it asks whether the WHOLE console has stopped, and the most dangerous
     absence in this app is a single endpoint dying under a healthy one.
     /api/robots answers, /api/robots/rover-01/telemetry does not, `starved`
     is false — and a sentence reading `rover-01 has never reported` goes on
     screen about a robot with 150 readings, sending an operator to check a
     ROS agent that is running perfectly. So a caller that has the read hands
     it over in `of`, and the sentence is withdrawn on that source alone. */
  const { starved } = useSupply();
  const asserting = kind !== 'missing' && kind !== 'withheld';
  const unread = asserting && (starved || (of !== undefined && noneAnswered(of)));
  const line = unread ? 'Nothing has been read.' : (children ?? blankLine(kind, words));
  const under = unread ? undefined : detail;

  return (
    <div
      className={styles.empty}
      data-kind={unread ? 'nothing' : kind}
      role={kind === 'missing' || kind === 'withheld' ? 'status' : undefined}
    >
      <span className={styles.emptyLine}>{line}</span>
      {under !== undefined && <span className={styles.emptyDetail}>{under}</span>}
      {action !== undefined && !unread && (
        <span className={styles.emptyAction}>
          <Button size="sm" onClick={action.onSelect}>
            {action.label}
          </Button>
        </span>
      )}
    </div>
  );
}

/**
 * A capability the platform genuinely has no data for — live video before the
 * WebRTC path exists, a capture still being processed, a viewport with no
 * session behind it. Hatched ground so the absence reads as deliberate rather
 * than broken, the state in mono, and nothing invented.
 *
 * THE BAR §11.10 is absolute about the viewport form: --void, one mono line
 * naming the subject, the endpoint and the state. No spinner, no skeleton, no
 * illustration, no centred CTA.
 */
export function Awaiting({
  label,
  detail,
  viewport = false,
}: {
  label: string;
  detail?: ReactNode;
  viewport?: boolean | undefined;
}) {
  return (
    <div className={styles.awaiting} data-void={viewport}>
      <span className={styles.awaitingLabel}>{label}</span>
      {detail !== undefined && <span className={styles.detail}>{detail}</span>}
    </div>
  );
}
